import { createContext, useContext, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";

import suspect1 from "../../img/suspect/suspect-1.png";
import suspect2 from "../../img/suspect/suspect-2.png";
import suspect3 from "../../img/suspect/suspect-3.png";
import suspect4 from "../../img/suspect/suspect-4.png";
import suspect5 from "../../img/suspect/suspect-5.png";

const GameContext = createContext();

/* ========================= CONSTANT ========================= */
const TIME_STORAGE_KEY = "GAME_TIME_REMAIN";
const ADMIN_TIME_KEY = "ADMIN_GAME_TIME";
const PLAYER_TEAM = "PLAYER_TEAM";
const PLAYER_INCODE = "PLAYER_INCODE";
const PLAYER_NAME = "PLAYER_NAME";
const PLAYER_KEY_CODE = "PLAYER_KEY_CODE";
const PLAYER_ROLE = "PLAYER_ROLE";

const TOTAL_GAME_TIME = 3600; // 기본값 (CourseList 조회 실패 시 폴백)
const MISSION_TOTAL = 10;

const FINAL_QUIZ_CODE = [
  "end_q1",
  "end_q2",
  "end_q3",
  "end_q4",
  "end_q5",
  "end_q6",
];
const LAST_QUIZ_CODE = ["last_q", "last_answer"];
const suspectImgs = [suspect1, suspect2, suspect3, suspect4, suspect5];

/* ========================= Back4App 설정 ========================= */
const PARSE_APP_ID = "iSAfLB3Yu649eDt2gvCbJg9LHU7RbsYhzpdzMLSR";
const PARSE_JS_KEY = "cXuIq8tOiwtopr2fNcEGkpBcnf21kPOLwF5bRqUH";
const PARSE_SERVER = "https://parseapi.back4app.com/";

/* ✅ 변경 가능한 설정값 */
const COURSE_CLASS = "CourseList"; // 관리자에서 저장하는 과정 테이블
const USER_CLASS = "spyx";
const PROG_PREFIX = "sp"; // 스파이x 접두어

/* ========================= PARSE ========================= */
function loadParse() {
  return new Promise((resolve) => {
    if (window.Parse && window._parseReady) return resolve(window.Parse);
    if (window.Parse && !window._parseReady) {
      window.Parse.initialize(PARSE_APP_ID, PARSE_JS_KEY);
      window.Parse.serverURL = PARSE_SERVER;
      window._parseReady = true;
      return resolve(window.Parse);
    }
    const s = document.createElement("script");
    s.src = "https://npmcdn.com/parse/dist/parse.min.js";
    s.onload = () => {
      window.Parse.initialize(PARSE_APP_ID, PARSE_JS_KEY);
      window.Parse.serverURL = PARSE_SERVER;
      window._parseReady = true;
      resolve(window.Parse);
    };
    document.head.appendChild(s);
  });
}

/* ========================= 유틸 ========================= */
// 입장 코드에서 회차 숫자 추출
// "spb001" → prefix="sp", roundAlpha="b" → round=2
const getRoundFromCode = (inCode, prefix) => {
  if (!inCode || !prefix) return null;
  const alpha = inCode.slice(prefix.length, prefix.length + 1); // "b"
  const round = alpha.charCodeAt(0) - 96; // a=1, b=2 ...
  return round > 0 ? round : null;
};

// 오늘 날짜 (YYYY-MM-DD, KST)
const todayKST = () => {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
};

export function GameProvider({ children }) {
  const location = useLocation();

  /* ========================= USER ========================= */
  const [introTeamName, setIntroTeamName] = useState(
    () => localStorage.getItem(PLAYER_TEAM) || "",
  );
  const [introInCodeV, setIntroInCodeV] = useState(
    () => localStorage.getItem(PLAYER_INCODE) || "",
  );
  const [playerName, setPlayerName] = useState(
    () => localStorage.getItem(PLAYER_NAME) || "",
  );
  const [userRole, setUserRole] = useState(
    () => localStorage.getItem(PLAYER_ROLE) || "",
  );
  const [playerKeyCode, setPlayerKeyCode] = useState(
    () => localStorage.getItem(PLAYER_KEY_CODE) || "",
  );

  useEffect(() => {
    if (introTeamName) localStorage.setItem(PLAYER_TEAM, introTeamName);
  }, [introTeamName]);
  useEffect(() => {
    if (introInCodeV) localStorage.setItem(PLAYER_INCODE, introInCodeV);
  }, [introInCodeV]);
  useEffect(() => {
    if (playerName) localStorage.setItem(PLAYER_NAME, playerName);
  }, [playerName]);
  useEffect(() => {
    if (userRole) localStorage.setItem(PLAYER_ROLE, userRole);
  }, [userRole]);
  useEffect(() => {
    if (playerKeyCode) localStorage.setItem(PLAYER_KEY_CODE, playerKeyCode);
  }, [playerKeyCode]);

  /* ========================= TIME ========================= */
  const [time, setTime] = useState(() => {
    // 새로고침 시 localStorage에서 복원
    const savedTime = localStorage.getItem(TIME_STORAGE_KEY);
    const savedAdmin = localStorage.getItem(ADMIN_TIME_KEY);
    // 남은시간이 있으면 복원, 없으면 관리자설정값, 없으면 기본값
    if (savedTime !== null) return Number(savedTime);
    if (savedAdmin !== null) return Number(savedAdmin);
    return TOTAL_GAME_TIME;
  });
  const [adminGameTime, setAdminGameTime] = useState(() => {
    const saved = localStorage.getItem(ADMIN_TIME_KEY);
    return saved !== null ? Number(saved) : TOTAL_GAME_TIME;
  });
  const [isRunning, setIsRunning] = useState(false);

  /**
   * CourseList에서 게임시간 조회
   * 입장 코드 → prefix + 회차 → 오늘 날짜 + prefix + round로 과정 조회 → duration(분) × 60
   */
  const fetchAdminTime = async (inCode) => {
    try {
      const Parse = await loadParse();

      // 1. 입장 코드에서 회차 추출
      const code = inCode || localStorage.getItem(PLAYER_INCODE) || "";
      const round = getRoundFromCode(code, PROG_PREFIX);
      const today = todayKST();

      console.log("⏱ 게임시간 조회:", { code, round, today });

      if (round !== null) {
        // 2. CourseList에서 오늘 날짜 + prefix + round로 과정 조회
        const Cls = Parse.Object.extend(COURSE_CLASS);
        const q = new Parse.Query(Cls);
        q.equalTo("prefix", PROG_PREFIX);
        q.equalTo("round", round);
        q.equalTo("useDate", today);
        q.descending("createdAt");
        const course = await q.first();

        if (course) {
          const durationMin = course.get("duration"); // 분 단위
          const durationSec = durationMin * 60; // 초 단위
          setTime(durationSec);
          setAdminGameTime(durationSec);
          localStorage.setItem(TIME_STORAGE_KEY, String(durationSec));
          localStorage.setItem(ADMIN_TIME_KEY, String(durationSec));
          console.log(
            `✅ 관리자 시간 적용: ${durationMin}분 (${durationSec}초)`,
          );
          return;
        } else {
          console.warn("⚠ CourseList 과정 없음 → 기본값 사용");
        }
      } else {
        console.warn("⚠ 코드에서 회차 추출 실패 → 기본값 사용");
      }

      // 3. 조회 실패 시 기본값
      setTime(TOTAL_GAME_TIME);
      setAdminGameTime(TOTAL_GAME_TIME);
      localStorage.setItem(ADMIN_TIME_KEY, String(TOTAL_GAME_TIME));
    } catch (e) {
      console.warn("게임시간 조회 실패:", e.message);
      setTime(TOTAL_GAME_TIME);
      setAdminGameTime(TOTAL_GAME_TIME);
    }
  };

  // fetchAdminTime은 LoginName에서 코드 확정 후 호출
  // 앱 시작 시에는 코드가 없으므로 호출하지 않음
  useEffect(() => {
    // localStorage에 저장된 시간이 있으면 복원 (새로고침 대응)
    const saved = localStorage.getItem(ADMIN_TIME_KEY);
    if (saved) {
      setAdminGameTime(Number(saved));
      setTime(Number(saved));
    }
  }, []);

  /* ========================= STAGES ========================= */
  const [clearedStages, setClearedStages] = useState(() => {
    const saved = localStorage.getItem("clearedStages");
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });

  useEffect(() => {
    localStorage.setItem("clearedStages", JSON.stringify([...clearedStages]));
  }, [clearedStages]);

  /* ========================= 이어하기 동기화 ========================= */
  useEffect(() => {
    if (!location.state?.isContinue) return;

    const savedTime = localStorage.getItem(TIME_STORAGE_KEY);
    if (savedTime !== null) {
      setTime(Number(savedTime));
      console.log("✅ Context 타이머 복원:", savedTime);
    }

    const savedAdminTime = localStorage.getItem(ADMIN_TIME_KEY);
    if (savedAdminTime !== null) {
      setAdminGameTime(Number(savedAdminTime));
    }

    const savedStages = localStorage.getItem("clearedStages");
    if (savedStages) {
      setClearedStages(new Set(JSON.parse(savedStages)));
      console.log("✅ Context 단서 복원:", savedStages);
    }
  }, [location.state]);

  /* ========================= 페이지 이동 시 Back4App 저장 ========================= */
  const prevPathRef = useRef(null);

  const saveToBack4App = async () => {
    const userId = localStorage.getItem("gameUserId");
    if (!userId) return;

    try {
      const Parse = await loadParse();
      const GameUser = Parse.Object.extend(USER_CLASS);
      const query = new Parse.Query(GameUser);
      const user = await query.get(userId);

      const currentTime = Number(localStorage.getItem(TIME_STORAGE_KEY) || 0);
      const currentStages = JSON.parse(
        localStorage.getItem("clearedStages") || "[]",
      );

      user.set("timeRemaining", currentTime);
      user.set("clearedStages", currentStages);
      await user.save();
      console.log(
        "✅ 페이지 이동 저장 - 시간:",
        currentTime,
        "단서:",
        currentStages,
      );
    } catch (e) {
      console.warn("저장 실패:", e.message);
    }
  };

  useEffect(() => {
    if (prevPathRef.current === null) {
      prevPathRef.current = location.pathname;
      return;
    }
    if (location.pathname === "/") {
      prevPathRef.current = location.pathname;
      return;
    }
    if (prevPathRef.current !== location.pathname) {
      saveToBack4App();
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  /* ========================= TIMER ========================= */
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => {
      setTime((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          localStorage.removeItem(TIME_STORAGE_KEY);
          alert("시간이 초과되었습니다.");
          window.location.href = "/";
          return 0;
        }
        const next = prev - 1;
        localStorage.setItem(TIME_STORAGE_KEY, next);
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  /* ========================= RESET ========================= */
  useEffect(() => {
    if (location.pathname === "/") {
      setIsRunning(false);

      localStorage.removeItem(TIME_STORAGE_KEY);
      localStorage.removeItem(ADMIN_TIME_KEY);
      localStorage.removeItem("clearedStages");
      localStorage.removeItem("correctCount");
      localStorage.removeItem("suspectScore");
      localStorage.removeItem("gameUserId");
      localStorage.removeItem(PLAYER_TEAM);
      localStorage.removeItem(PLAYER_INCODE);
      localStorage.removeItem(PLAYER_NAME);
      localStorage.removeItem(PLAYER_KEY_CODE);
      localStorage.removeItem(PLAYER_ROLE);

      setIntroTeamName("");
      setIntroInCodeV("");
      setPlayerName("");
      setPlayerKeyCode("");
      setUserRole("");
      setCorrectCount(0);
      setSuspectScore(0);
      setClearedStages(new Set());
      setHintView("NONE");
      setHintIndex(null);
      setBackgroundNone(false);

      // 홈으로 돌아오면 시간 초기화 (다음 입장 시 LoginName에서 새로 조회)
      setTime(TOTAL_GAME_TIME);
      setAdminGameTime(TOTAL_GAME_TIME);
    }
  }, [location.pathname]);

  /* ========================= SCORE ========================= */
  const [correctCount, setCorrectCount] = useState(
    () => Number(localStorage.getItem("correctCount")) || 0,
  );
  const [suspectScore, setSuspectScore] = useState(
    () => Number(localStorage.getItem("suspectScore")) || 0,
  );

  useEffect(() => {
    localStorage.setItem("correctCount", correctCount);
  }, [correctCount]);
  useEffect(() => {
    localStorage.setItem("suspectScore", suspectScore);
  }, [suspectScore]);

  const increaseMissionOnce = (key) => {
    setClearedStages((prev) => {
      if (prev.has(key)) return prev;
      if (LAST_QUIZ_CODE.includes(key)) return prev;
      if (prev.size >= MISSION_TOTAL) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  /* ========================= HINT ========================= */
  const [hintView, setHintView] = useState("NONE");
  const [hintIndex, setHintIndex] = useState(null);
  const [backgroundNone, setBackgroundNone] = useState(false);

  const openHint = () => {
    setHintIndex(null);
    setHintView("LIST");
    setBackgroundNone(true);
  };
  const openHintDetail = (idx) => {
    setHintIndex(idx);
    setHintView("DETAIL");
    setBackgroundNone(true);
  };
  const openSuspectDetail = () => {
    setHintIndex("suspect");
    setHintView("SUSPECT");
    setBackgroundNone(true);
  };

  const backFromHint = () => {
    if (hintView === "DETAIL" || hintView === "SUSPECT") {
      setHintIndex(null);
      setHintView("LIST");
    } else {
      setHintIndex(null);
      setHintView("NONE");
      setBackgroundNone(false);
    }
  };

  const closeHint = () => {
    setHintIndex(null);
    setHintView("NONE");
    setBackgroundNone(false);
  };
  const backBtnView = hintView !== "NONE";

  /* ========================= HELPERS ========================= */
  const isFinalQuiz = (code) => FINAL_QUIZ_CODE.includes(code);
  const isLastQuiz = (code) => LAST_QUIZ_CODE.includes(code);

  return (
    <GameContext.Provider
      value={{
        introTeamName,
        setIntroTeamName,
        introInCodeV,
        setIntroInCodeV,
        playerName,
        setPlayerName,
        playerKeyCode,
        setPlayerKeyCode,
        userRole,
        setUserRole,

        time,
        setTime,
        TOTAL_GAME_TIME: adminGameTime, // ✅ 관리자 CourseList에서 가져온 시간
        startTimer: () => setIsRunning(true),
        pauseTimer: () => setIsRunning(false),
        syncTimer: (clearTime) => {
          setTime(clearTime);
          // ⚠ setAdminGameTime 건드리지 않음 — TOTAL_GAME_TIME은 관리자 설정 원래값 유지
          localStorage.setItem(TIME_STORAGE_KEY, String(clearTime));
        },

        // ✅ 입장 코드 확정 후 게임시간 재조회 (로그인/입장 시 호출)
        fetchAdminTime,

        MISSION_TOTAL,
        clearedStages,
        setClearedStages,
        increaseMissionOnce,

        hintView,
        hintIndex,
        backBtnView,
        openHint,
        openHintDetail,
        openSuspectDetail,
        backFromHint,
        closeHint,
        backgroundNone,

        isFinalQuiz,
        isLastQuiz,

        correctCount,
        setCorrectCount,
        suspectScore,
        setSuspectScore,
        suspectImgs,
      }}
    >
      {children}
    </GameContext.Provider>
  );
}

export const useGame = () => useContext(GameContext);
export const useTimer = () => useContext(GameContext); // ✅ 호환용
